import * as Sentry from "@sentry/angular";
import { trackOpenPanelProductEvent } from "src/app/support/openpanel";

export const PMS_USER_ACTION_METRIC = "pms_user_action";

export type SentryMetricOutcome =
  | "success"
  | "error"
  | "attempted"
  | "cancelled";

export type SentryMetricArea =
  | "api"
  | "app"
  | "calendar"
  | "support"
  | "board"
  | "scanner"
  | "warehouse"
  | "analytics"
  | "customer_detail"
  | "patient_detail"
  | "patient_book"
  | "clinic_diary"
  | "visit"
  | "checkout"
  | "cart"
  | "registry"
  | "invoice"
  | "fiscal"
  | "page_detail";

export type PageDetailCompactToolbarMetricSource =
  | "info_banner_suggestion"
  | "info_banner_active"
  | "preferences";

export type PageDetailCompactIconsInfoDismissBanner = Extract<
  PageDetailCompactToolbarMetricSource,
  "info_banner_active" | "info_banner_suggestion"
>;

export type SentryActionMetricAttributes = {
  area: SentryMetricArea;
  entity: string;
  action: string;
  outcome: SentryMetricOutcome;
  item_type?: string;
  channel?: string;
  view?: string;
  filter_type?: string;
  setting?: string;
  direction?: string;
  dashboard_mode?: string;
  tab_group_by?: string;
  panel_state?: string;
  source_column?: string;
  destination_column?: string;
  card_type?: string;
  platform?: string;
  scan_type?: string;
  consumer?: string;
  lookup_scope?: string;
  good_type?: string;
  match_source?: string;
  entry_point?: string;
  tab?: string;
  record_type?: string;
  origin_type?: string;
  flow?: string;
  mode?: string;
  granularity?: string;
  value_series?: string;
  lookup_result?: string;
};

export function countUserAction(
  attributes: SentryActionMetricAttributes
): void {
  Sentry.metrics.count(PMS_USER_ACTION_METRIC, 1, {
    attributes: attributes as Record<string, string>
  });

  const { area, entity, action, outcome, ...rest } = attributes;

  trackOpenPanelProductEvent(area, entity, action, {
    outcome,
    ...rest
  });
}

export function deriveBackendEntity(className: string): string {
  const withoutBackend: string = className.replace(/Backend$/, "");
  const snakeCase: string = withoutBackend
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/-/g, "_")
    .toLowerCase();

  return snakeCase || "unknown";
}

export function deriveBackendAction(methodName: string): string {
  const snakeCase: string = methodName
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/-/g, "_")
    .toLowerCase();

  return snakeCase || "unknown";
}

export function countBackendUserAction(
  className: string,
  methodName: string,
  outcome: Extract<SentryMetricOutcome, "success" | "error">
): void {
  countUserAction({
    area: "api",
    entity: deriveBackendEntity(className),
    action: deriveBackendAction(methodName),
    outcome
  });
}

type FlowActionAttributes = Partial<
  Omit<SentryActionMetricAttributes, "area" | "entity" | "action" | "outcome">
>;

export function countFlowAction(
  area: SentryMetricArea,
  entity: string,
  action: string,
  outcome: SentryMetricOutcome = "success",
  attributes?: FlowActionAttributes
): void {
  countUserAction({
    area,
    entity,
    action,
    outcome,
    ...attributes
  });
}

export function trackPageDetailCompactToolbarChange(
  enabled: boolean,
  entryPoint: PageDetailCompactToolbarMetricSource
): void {
  countFlowAction(
    "page_detail",
    "toolbar",
    enabled ? "enable_compact_icons" : "disable_compact_icons",
    "success",
    { entry_point: entryPoint }
  );
}

export function trackPageDetailCompactIconsInfoDismiss(
  banner: PageDetailCompactIconsInfoDismissBanner
): void {
  countFlowAction(
    "page_detail",
    "toolbar",
    "dismiss_compact_icons_info",
    "success",
    { entry_point: banner }
  );
}
